/**
 * External Services Catalog API Service
 * Fetches the catalog of all external services, AI providers, and integrations
 */

import axios from 'axios';
import { config } from '@/src/config';

const API_URL = config.weaveinApi.baseUrl;

const getHeaders = () => {
  const token = localStorage.getItem('auth_token');
  const cboId = localStorage.getItem('active_cbo_id');
  return {
    'Authorization': `Bearer ${token}`,
    'X-CBO-ID': cboId,
  };
};

// Types

export interface ServiceModel {
  model_id: string;
  display_name: string;
  use_cases: string[];
  pricing_input_per_1m?: number | null;
  pricing_output_per_1m?: number | null;
  pricing_notes?: string | null;
}

export interface ServiceUsageStats {
  total_invocations: number;
  invocations_last_30d: number;
  invocations_last_7d: number;
  total_tokens: number;
  total_cost_usd: number;
  cost_last_30d: number;
  avg_latency_ms?: number | null;
  success_rate?: number | null;
  last_used?: string | null;
}

export interface ExternalService {
  service_id: string;
  name: string;
  provider: string;
  category: string;
  description: string;
  dependency_level: string;
  dependency_reason: string;
  env_vars: string[];
  models: ServiceModel[];
  usage: ServiceUsageStats;
  metering_type: string;
  metering_notes: string;
  files_using: string[];
  status: string;
  hipaa_eligible: boolean;
  documentation_url?: string | null;
}

export interface CatalogSummary {
  total_services: number;
  total_ai_cost_30d: number;
  total_communication_cost_30d: number;
  total_invocations_30d: number;
  services_by_category: Record<string, number>;
  top_cost_services: Array<{ service: string; cost_30d: number; invocations_30d: number }>;
}

export interface ExternalServicesCatalogResponse {
  summary: CatalogSummary;
  services: ExternalService[];
  generated_at: string;
}

export interface ServiceCategory {
  id: string;
  name: string;
  icon: string;
}

export interface DailyUsageEntry {
  date: string;
  model: string;
  invocations: number;
  tokens: number;
  cost_usd: number;
  avg_latency_ms: number | null;
  successes: number;
  failures: number;
}

// API functions

export async function fetchExternalServicesCatalog(
  category?: string,
  dependencyLevel?: string
): Promise<ExternalServicesCatalogResponse> {
  const params = new URLSearchParams();
  if (category) params.set('category', category);
  if (dependencyLevel) params.set('dependency_level', dependencyLevel);

  const response = await axios.get(
    `${API_URL}/api/external-services/catalog?${params.toString()}`,
    { headers: getHeaders() }
  );
  return response.data;
}

export async function fetchServiceCategories(): Promise<{ categories: ServiceCategory[] }> {
  const response = await axios.get(`${API_URL}/api/external-services/categories`, {
    headers: getHeaders(),
  });
  return response.data;
}

export async function fetchServiceUsageDetail(
  serviceId: string,
  days: number = 30
): Promise<{ service_id: string; days: number; daily_usage: DailyUsageEntry[] }> {
  const response = await axios.get(
    `${API_URL}/api/external-services/${serviceId}/usage-detail?days=${days}`,
    { headers: getHeaders() }
  );
  return response.data;
}
